import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { AnswerFrequencyType } from '@/constants/enum';
import { ANSWER_DATES_STORE_KEY } from '@/constants/storeKeys';

dayjs.extend(isBetween);

interface State {
  answer_dates: Array<any>;
  answer_dates_by_date: Array<any>;
  answer_date: Object;
  selected_answer_date: string;
  is_auto_sign_in: boolean;
  // 回答リンク経由アクセス時のリダイレクト先パス（ルートガードで使用）
  auto_sign_in_redirect_path: string;
}

export const useAnswerDateStore = defineStore(ANSWER_DATES_STORE_KEY, {
  state: (): State => ({
    answer_dates: [],
    answer_dates_by_date: [],
    answer_date: {},
    selected_answer_date: '',
    is_auto_sign_in: false,
    auto_sign_in_redirect_path: '',
  }),

  getters: {
  },
  actions: {
    // 回答可能な質問紙のidを渡す
    getAvailableQuestionnaireID() {
      function isBetweenStartEnd(val: any): any {
        const start_date = dayjs(val.start_date + ` ${val.questionnaire.start_time ?? '00:00:00'}`)
          .subtract(val.questionnaire.days_before, 'd');
        const end_date = dayjs(val.start_date + ` ${val.questionnaire.end_time ?? '23:59:59'}`)
          .add(val.questionnaire.days_after, 'd');
        return dayjs().isBetween(start_date, end_date, 'minute', '[]');
      }
      const available_questionnaire = this.answer_dates
        .filter((val: any) => val.status == 'not_answered')
        .filter(isBetweenStartEnd);
      if (available_questionnaire.length > 0) {
        return available_questionnaire[0].id;
      }
      return 0;
    },
    // 回答可能な質問紙をすべて完了したかを渡す
    getTodayComplete() {
      function isBetweenStartEnd(val: any): any {
        const start_date = dayjs(val.start_date + ` ${val.questionnaire.start_time ?? '00:00:00'}`)
          .subtract(val.questionnaire.days_before, 'd');
        const end_date = dayjs(val.start_date + ` ${val.questionnaire.end_time ?? '23:59:59'}`)
          .add(val.questionnaire.days_after, 'd');
        return dayjs().isBetween(start_date, end_date, 'minute', '[]');
      }
      const today_questionnaire = this.answer_dates.filter(isBetweenStartEnd);
      const today_complete_questionnaire = this.answer_dates
        .filter((val: any) => val.complete_date !== null)
        .filter(isBetweenStartEnd);
      return today_questionnaire.length > 0 && today_questionnaire.length === today_complete_questionnaire.length;
    },
    // 選択した日付の回答日を返す
    getAnswerDatesByDate(date: string): Array<any> {
      return this.answer_dates.filter((answer_date: any) => answer_date.start_date === date);
    },
    // 未回答の回答日を返す
    getUnansweredAnswerDates(answer_dates: Array<any>): Array<any> {
      return answer_dates.filter((answer_date: any) => answer_date.complete_date === null);
    },
    // 回答済みの回答日を返す
    getCompletedAnswerDates(answer_dates: Array<any>): Array<any> {
      return answer_dates.filter((answer_date: any) => answer_date.complete_date !== null);
    },
    // 各質問紙の回答状況を返す
    // start_dateとcomplete_dateの組み合わせで、未回答、回答済み、回答不可の3つに分ける
    getAnswersStatus(): object {
      let answers_status: any = {
        not_answered: [],
        answered: [],
        not_available_yet: [],
        anytime_answerable: {},
      };
      this.answer_dates.forEach((answer_date: any) => {
        // 随時回答の場合
        if (answer_date.questionnaire.frequency_type === AnswerFrequencyType.ANYTIME) {
          // 随時回答の場合、回答状況に背景色属性を与える。背景色属性は質問紙ごとに設定するため、質問紙IDをキーにする。
          if (!answers_status['anytime_answerable'].hasOwnProperty(answer_date.questionnaire.id)) {
            answers_status['anytime_answerable'][answer_date.questionnaire.id] = {
              'btn_color': answer_date.questionnaire.btn_color,
              'start_dates': [],
            };
          }
          answers_status['anytime_answerable'][answer_date.questionnaire.id].start_dates.push(answer_date.start_date);
        } else {
          // 質問紙の実際の回答可能期間を計算
          const start_datetime = dayjs(
            answer_date.start_date + ` ${answer_date.questionnaire.start_time ?? '00:00:00'}`
          ).subtract(answer_date.questionnaire.days_before, 'd');
          const end_datetime = dayjs(
            answer_date.start_date + ` ${answer_date.questionnaire.end_time ?? '23:59:59'}`
          ).add(answer_date.questionnaire.days_after, 'd');

          // 回答可能期間前の場合
          if (dayjs().isBefore(start_datetime)) {
            answers_status['not_available_yet'].push(answer_date.start_date);
          }
          // 回答済みの場合
          else if (answer_date.complete_date !== null) {
            answers_status['answered'].push(answer_date.start_date);
          }
          // 未回答の場合
          else {
            answers_status['not_answered'].push(answer_date.start_date);
          }
        }
      })

      // 重複を削除（随時回答は回答数を表示するために重複を消さない）
      answers_status['not_answered'] = [...new Set(answers_status['not_answered'])];
      answers_status['answered'] = [...new Set(answers_status['answered'])];
      answers_status['not_available_yet'] = [...new Set(answers_status['not_available_yet'])];

      // 同じ日付に回答済みと未回答両方存在する場合、未回答状態として扱う
      answers_status['answered'] = answers_status['answered'].filter((val: any) => !answers_status['not_answered'].includes(val));

      return answers_status;
    },
  },
  persist: true,
});

